import { useEffect, useState } from "react";
import { apiGet } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { FileText, Table2, Users, Download } from "lucide-react";

const STREETS = ["JALAN 1/1","JALAN 1/2","JALAN 1/3","JALAN 1/4","JALAN 1/5","JALAN 1/6","JALAN 1/7","JALAN 1/8"];
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";
const PERIODS = [
  { key: "monthly",  label: "This Month" },
  { key: "6months",  label: "Last 6 Months" },
  { key: "all",      label: "All Time" },
  { key: "custom",   label: "Custom Range" },
];

function fmtMonth(m) {
  if (!m) return "—";
  const [yr, mo] = m.split("-");
  const labels = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${labels[parseInt(mo,10)-1]} ${yr}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-MY", { day:"2-digit", month:"short", year:"numeric" });
}

function downloadCSV(rows, headers, filename) {
  const escape = v => `"${String(v ?? "").replace(/"/g,'""')}"`;
  const lines = [headers.map(escape).join(","), ...rows.map(r => r.map(escape).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function periodLabel(period, startDate, endDate) {
  if (period === "custom" && startDate && endDate) {
    return `${fmtDate(startDate)} – ${fmtDate(endDate)}`;
  }
  return PERIODS.find(p => p.key === period)?.label || "All Time";
}

function periodFilenameSuffix(period, startDate, endDate) {
  if (period === "custom" && startDate && endDate) {
    return `${startDate}_to_${endDate}`;
  }
  return PERIODS.find(p => p.key === period)?.label.replace(/ /g, "-") || "all";
}

function buildReportQuery(period, startDate, endDate) {
  const params = new URLSearchParams({ period });
  if (period === "custom") {
    params.set("start_date", startDate);
    params.set("end_date", endDate);
  }
  return params.toString();
}

function validateCustomRange(period, startDate, endDate) {
  if (period !== "custom") return true;
  if (!startDate || !endDate) {
    alert("Please select both a start date and an end date.");
    return false;
  }
  if (startDate > endDate) {
    alert("Start date must be on or before end date.");
    return false;
  }
  return true;
}

function brandHeader(doc, title, period, startDate, endDate) {
  doc.setFillColor(122, 58, 24);
  doc.rect(0, 0, 210, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16); doc.setFont("helvetica", "bold");
  doc.text("PERCASA", 14, 11);
  doc.setFontSize(8); doc.setFont("helvetica", "normal");
  doc.text("Residence Management", 14, 17);
  doc.setFontSize(12); doc.setFont("helvetica", "bold");
  doc.text(title, 14, 25);
  doc.setTextColor(80, 80, 80);
  doc.setFontSize(8);
  const pl = periodLabel(period, startDate, endDate);
  doc.text(`Period: ${pl}  |  Generated: ${new Date().toLocaleDateString("en-MY")}`, 14, 33);
}

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [outstanding, setOutstanding] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState("");
  const currentYear = new Date().getFullYear();
  const [mappingStreet, setMappingStreet] = useState(STREETS[0]);
  const [mappingYear, setMappingYear] = useState(Math.max(currentYear, 2026));
  const [mappingReport, setMappingReport] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(true);

  useEffect(() => {
    apiGet("/stats").then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const query = buildReportQuery(period, startDate, endDate);
    apiGet(`/reports/outstanding?${query}`)
      .then(r => setOutstanding(r.data))
      .catch(() => setOutstanding([]));
  }, [period, startDate, endDate]);

  // Scoped to a single street at a time (instead of loading every street/house at once)
  // to keep this report fast as the number of registered houses grows.
  useEffect(() => {
    setMappingLoading(true);
    apiGet(`/reports/maintenance-mapping?street=${encodeURIComponent(mappingStreet)}&year=${mappingYear}`)
      .then(r => setMappingReport(r.data))
      .catch(() => setMappingReport(null))
      .finally(() => setMappingLoading(false));
  }, [mappingStreet, mappingYear]);

  const yearOptions = Array.from(
    { length: Math.max(currentYear, 2026) - 2026 + 1 },
    (_, i) => 2026 + i
  );
  const monthShort = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  async function exportPaymentsPDF() {
    if (!validateCustomRange(period, startDate, endDate)) return;
    setExporting("pdf-payments");
    try {
      const res = await apiGet(`/reports/payments?${buildReportQuery(period, startDate, endDate)}`);
      const data = res.data;
      const doc = new jsPDF();
      brandHeader(doc, "Payment History Report", period, startDate, endDate);
      autoTable(doc, {
        startY: 38,
        head: [["Resident", "House No.", "Category", "Month", "Amount (RM)", "Date", "Status"]],
        body: data.map(p => [
          p.resident_name || "—", p.unit || "—", p.category || "—",
          fmtMonth(p.month), (p.amount || 0).toFixed(2),
          fmtDate(p.submitted_at), (p.status || "").toUpperCase()
        ]),
        headStyles: { fillColor: [122, 58, 24], fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [253, 246, 240] },
        styles: { cellPadding: 3 },
      });
      const pl = periodFilenameSuffix(period, startDate, endDate);
      doc.save(`percasa-payment-history-${pl}.pdf`);
    } catch (e) { alert("Export failed. Please try again."); }
    finally { setExporting(""); }
  }

  async function exportOutstandingCSV() {
    if (!validateCustomRange(period, startDate, endDate)) return;
    setExporting("csv-outstanding");
    try {
      const res = await apiGet(`/reports/outstanding?${buildReportQuery(period, startDate, endDate)}`);
      const data = res.data;
      downloadCSV(
        data.map(o => [o.unit || "—", o.resident_name || "—", o.months_owed, (o.amount || 0).toFixed(2)]),
        ["House No.", "Resident", "Months Owed", "Amount (RM)"],
        `percasa-outstanding-${periodFilenameSuffix(period, startDate, endDate)}.csv`
      );
    } catch (e) { alert("Export failed. Please try again."); }
    finally { setExporting(""); }
  }

  async function exportResidentsPDF() {
    setExporting("pdf-residents");
    try {
      const res = await apiGet("/reports/residents");
      const data = res.data;
      const doc = new jsPDF();
      brandHeader(doc, "Resident Register", "all");
      autoTable(doc, {
        startY: 38,
        head: [["Name", "House No.", "Street", "Email", "Phone", "Status", "Joined"]],
        body: data.map(r => [
          r.name || "—", r.unit || "—", r.street_number || "—",
          r.email || "—", r.phone || "—",
          (r.status || "").toUpperCase(), fmtDate(r.created_at)
        ]),
        headStyles: { fillColor: [122, 58, 24], fontSize: 7 },
        bodyStyles: { fontSize: 7 },
        alternateRowStyles: { fillColor: [253, 246, 240] },
        styles: { cellPadding: 2.5 },
      });
      doc.save("percasa-resident-register.pdf");
    } catch (e) { alert("Export failed. Please try again."); }
    finally { setExporting(""); }
  }

  if (loading) return <div style={{ color: "var(--text2)", fontSize: 13, padding: 20 }}>Loading reports...</div>;

  const monthlyData = stats?.monthly_data || [];

  return (
    <div>
      {/* Chart */}
      <div className="section-panel" style={{ marginBottom: 16 }} data-testid="reports-chart">
        <div className="section-head">
          <div className="section-title">Collection Rate by Month</div>
        </div>
        <div style={{ padding: "16px 18px" }}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthlyData} barSize={40}>
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "var(--text2)", fontFamily: "var(--mono)" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip
                formatter={v => [`RM ${v.toLocaleString()}`, "Collection"]}
                contentStyle={{ fontSize: 11, background: "#fff", border: "1px solid var(--border)", borderRadius: 6 }}
              />
              <Bar dataKey="amount" radius={[4,4,0,0]}>
                {monthlyData.map((entry, i) => (
                  <Cell key={i} fill={i === monthlyData.length - 1 ? "var(--accent)" : "var(--brand-mid)"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Maintenance fee mapping: which houses paid which months, for one street/year at a time */}
      <div className="section-panel" style={{ marginBottom: 16 }} data-testid="maintenance-mapping-report">
        <div className="section-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div className="section-title">Maintenance Fee Mapping</div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--text2)" }}>
              Street
              <select
                className="form-input"
                style={{ fontSize: 11, height: 28, width: "auto", padding: "2px 8px" }}
                value={mappingStreet}
                onChange={e => setMappingStreet(e.target.value)}
                data-testid="mapping-report-street"
              >
                {STREETS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--text2)" }}>
              Year
              <select
                className="form-input"
                style={{ fontSize: 11, height: 28, width: "auto", padding: "2px 8px" }}
                value={mappingYear}
                onChange={e => setMappingYear(Number(e.target.value))}
                data-testid="mapping-report-year"
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
        {mappingLoading ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>Loading maintenance mapping...</div>
        ) : !mappingReport ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>Unable to load maintenance mapping</div>
        ) : mappingReport.rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>No houses found for {mappingReport.street}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="percasa-table" data-testid="maintenance-mapping-table">
              <thead>
                <tr>
                  <th>House No.</th>
                  <th>Street</th>
                  {monthShort.map(m => <th key={m} style={{ textAlign: "center" }}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {mappingReport.rows.map(row => (
                  <tr key={row.house_alias}>
                    <td style={{ fontWeight: 600 }}>
                      <span className="mono">{row.unit}</span>
                    </td>
                    <td style={{ whiteSpace: "nowrap", fontSize: 11, color: "var(--text2)" }}>{mappingStreet.replace('JALAN ', '')}</td>
                    {(mappingReport.months || []).map(m => {
                      const paid = row.months?.[m] === 1;
                      return (
                        <td key={m} style={{ textAlign: "center", padding: 0 }}>
                          <span
                            className="mono"
                            title={paid ? "Paid" : "Not paid"}
                            style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: 28, height: 24, fontSize: 12, fontWeight: 800, borderRadius: 4,
                              background: paid ? "#16A34A" : "#F59E0B",
                              color: paid ? "#FFFFFF" : "#78350F",
                            }}
                          >
                            {paid ? 1 : 0}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Outstanding Table */}
        <div className="section-panel" data-testid="outstanding-table">
          <div className="section-head">
            <div className="section-title">Top 10 Outstanding Houses</div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="percasa-table">
              <thead><tr><th>Resident</th><th>House No.</th><th>Months Owed</th><th>Amount</th></tr></thead>
              <tbody>
                {outstanding.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--text2)", padding: 20, fontSize: 12 }}>No outstanding houses</td></tr>
                ) : outstanding.slice(0, 10).map((o, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{o.resident_name || "—"}</td>
                    <td><span className="mono">{o.unit || "—"}</span></td>
                    <td>
                      <span className={`badge ${o.months_owed >= 3 ? "rejected" : "pending"}`}>
                        {o.months_owed} {o.months_owed === 1 ? "month" : "months"}
                      </span>
                    </td>
                    <td><span className="mono">RM {o.amount?.toFixed(2)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Export Reports */}
        <div className="section-panel" data-testid="export-reports">
          <div className="section-head">
            <div className="section-title">Export Reports</div>
          </div>
          <div style={{ padding: "14px 18px" }}>
            {/* Period Selector */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>
                Report Period
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {PERIODS.map(p => (
                  <button key={p.key}
                    className={`btn ${period === p.key ? "btn-primary" : ""}`}
                    style={{ fontSize: 10, padding: "4px 10px" }}
                    onClick={() => setPeriod(p.key)}
                    data-testid={`period-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {period === "custom" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      data-testid="export-start-date"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">End Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      data-testid="export-end-date"
                    />
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button className="btn" style={{ justifyContent: "flex-start", gap: 10, padding: "10px 12px" }}
                onClick={exportPaymentsPDF}
                disabled={exporting === "pdf-payments"}
                data-testid="export-payment-history-pdf">
                <FileText size={14} style={{ color: "var(--brand)", flexShrink: 0 }} />
                <div style={{ textAlign: "left", flex: 1 }}>
                  <div style={{ fontSize: 12 }}>Payment History PDF</div>
                  <div style={{ fontSize: 10, color: "var(--text2)" }}>Full payment records · {periodLabel(period, startDate, endDate)}</div>
                </div>
                <Download size={12} style={{ color: exporting === "pdf-payments" ? "var(--brand)" : "var(--text2)", flexShrink: 0 }} />
              </button>

              <button className="btn" style={{ justifyContent: "flex-start", gap: 10, padding: "10px 12px" }}
                onClick={exportOutstandingCSV}
                disabled={exporting === "csv-outstanding"}
                data-testid="export-outstanding-csv">
                <Table2 size={14} style={{ color: "var(--brand)", flexShrink: 0 }} />
                <div style={{ textAlign: "left", flex: 1 }}>
                  <div style={{ fontSize: 12 }}>Outstanding CSV</div>
                  <div style={{ fontSize: 10, color: "var(--text2)" }}>Houses with overdue payments · {periodLabel(period, startDate, endDate)}</div>
                </div>
                <Download size={12} style={{ color: exporting === "csv-outstanding" ? "var(--brand)" : "var(--text2)", flexShrink: 0 }} />
              </button>

              <button className="btn" style={{ justifyContent: "flex-start", gap: 10, padding: "10px 12px" }}
                onClick={exportResidentsPDF}
                disabled={exporting === "pdf-residents"}
                data-testid="export-resident-register-pdf">
                <Users size={14} style={{ color: "var(--brand)", flexShrink: 0 }} />
                <div style={{ textAlign: "left", flex: 1 }}>
                  <div style={{ fontSize: 12 }}>Resident Register PDF</div>
                  <div style={{ fontSize: 10, color: "var(--text2)" }}>All resident details</div>
                </div>
                <Download size={12} style={{ color: exporting === "pdf-residents" ? "var(--brand)" : "var(--text2)", flexShrink: 0 }} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {stats && (
        <div className="stat-cards" style={{ marginTop: 16 }} data-testid="report-stats">
          {[
            { label: "Total Residents",   val: stats.total_residents,   color: "var(--brand)" },
            { label: "Total Outstanding", val: `RM ${(stats.outstanding || 0).toFixed(2)}`, color: stats.outstanding > 0 ? "var(--danger)" : "var(--text)" },
            { label: "Approved Payments", val: stats.payment_breakdown?.approved, color: "var(--success)" },
            { label: "Pending Payments",  val: stats.payment_breakdown?.pending,  color: "var(--pending)" },
          ].map(s => (
            <div className="stat-card" key={s.label}>
              <div className="card-label">{s.label}</div>
              <div className="card-value" style={{ color: s.color }}>{s.val || 0}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
